import dayjs from "dayjs";
import * as yup from "yup";
import {
  PROMOTION_EVENTS_TIMEZONE,
  promotionCalendarDateKey,
  zonedDateTimeToUtc,
} from "../../../constants/promotionEvents";

export const DEFAULT_PROMOTION_EVENT_HOUR = 10;

export const defaultPromotionEventTime = () =>
  dayjs().hour(DEFAULT_PROMOTION_EVENT_HOUR).minute(0).second(0).millisecond(0);

const padUnit = (value) => String(value).padStart(2, "0");

export const pickerCalendarDay = (dateValue) => {
  const date = dayjs(dateValue);
  if (!date.isValid()) return null;
  return `${date.year()}-${padUnit(date.month() + 1)}-${padUnit(date.date())}`;
};

export const promotionTodayPickerValue = (now = new Date()) => {
  const key = promotionCalendarDateKey(now);
  if (!key) return dayjs();
  const [year, month, day] = key.split("-").map(Number);
  return dayjs(new Date(year, month - 1, day));
};

export const promotionInstantToPicker = (iso) => {
  if (!iso) return null;
  const key = promotionCalendarDateKey(iso);
  if (!key) return null;
  const [year, month, day] = key.split("-").map(Number);
  const clock = new Intl.DateTimeFormat("en-GB", {
    timeZone: PROMOTION_EVENTS_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const read = (type) =>
    Number(clock.find((part) => part.type === type)?.value || 0);
  return dayjs(
    new Date(year, month - 1, day, read("hour") % 24, read("minute"))
  );
};

export const combineDateAndTimeIso = (dateValue, timeValue) => {
  const date = dayjs(dateValue);
  const time = dayjs(timeValue);
  if (!date.isValid() || !time.isValid()) return null;
  return zonedDateTimeToUtc(
    date.year(),
    date.month() + 1,
    date.date(),
    time.hour(),
    time.minute(),
    0,
    0
  ).toISOString();
};

export const hasEventTimeValue = (timeValue) =>
  Boolean(timeValue && dayjs(timeValue).isValid());

export const resolvePromotionTimePickerValue = ({
  value,
  open = false,
  applyDefaultOnOpen = false,
} = {}) => {
  if (hasEventTimeValue(value)) return value;
  if (open && applyDefaultOnOpen) return defaultPromotionEventTime();
  return null;
};

export const formatUtcEventDate = (
  iso,
  { withYear = true, shortYear = false, weekday = false } = {}
) => {
  if (!iso) return "—";
  const parsed = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  if (weekday) {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: PROMOTION_EVENTS_TIMEZONE,
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(parsed);
  }
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: PROMOTION_EVENTS_TIMEZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).formatToParts(parsed);
  const read = (type) => parts.find((part) => part.type === type)?.value || "";
  const day = read("day");
  const month = read("month");
  if (!withYear) return `${day} ${month}`;
  const year = shortYear ? read("year").slice(-2) : read("year");
  return shortYear ? `${day} ${month} ${year}` : `${day} ${month}, ${year}`;
};

export const formatPromotionEventTime = (
  iso,
  { hour12 = false, empty = "—" } = {}
) => {
  if (!iso) return empty;
  const parsed = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(parsed.getTime())) return empty;
  return parsed.toLocaleTimeString("en-GB", {
    timeZone: PROMOTION_EVENTS_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12,
  });
};

export const combineEventDatePayload = (dateValue, timeValue) => {
  const date = dayjs(dateValue);
  if (!date.isValid()) {
    return { eventDate: null, hasEventTime: false };
  }
  if (hasEventTimeValue(timeValue)) {
    return {
      eventDate: combineDateAndTimeIso(dateValue, timeValue),
      hasEventTime: true,
    };
  }
  return {
    eventDate: date.format("YYYY-MM-DD"),
    hasEventTime: false,
  };
};

export const isPromotionEventDateTimeInPast = (
  dateValue,
  timeValue,
  now = new Date()
) => {
  const iso = combineDateAndTimeIso(dateValue, timeValue);
  if (!iso) return false;
  return new Date(iso).getTime() < new Date(now).getTime();
};

export const promotionEventDateSchema = () =>
  yup
    .mixed()
    .nullable()
    .required("Date is required")
    .test("valid-date", "Date is required", (value) =>
      value ? dayjs(value).isValid() : false
    )
    .test(
      "not-before-today",
      "Date cannot be before today",
      (value) =>
        !value ||
        !dayjs(value).isValid() ||
        pickerCalendarDay(value) >= promotionCalendarDateKey(new Date())
    );

export const promotionEventTimeSchema = ({ required = false } = {}) =>
  yup
    .mixed()
    .nullable()
    .test("required-time", "Time is required", (value) =>
      required ? Boolean(value && dayjs(value).isValid()) : true
    )
    .test("valid-time", "Time is required", (value) => {
      if (!value) return !required;
      return dayjs(value).isValid();
    })
    .test(
      "not-in-the-past",
      "Time cannot be in the past",
      function notInThePast(value) {
        const eventDate = this.parent?.eventDate;
        if (!value || !eventDate) return true;
        return !isPromotionEventDateTimeInPast(eventDate, value);
      }
    );

export const syncEventDateField = async (formik, value) => {
  await formik.setFieldValue("eventDate", value);
  if (formik.values.eventTime) {
    await formik.validateField("eventTime");
  }
};
